import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Award, ChevronRight, Download, FileText, Home } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import useLocale from '../contexts/LocaleContext';
import { useLocationRanking } from '../hooks/useAnalytics';
import { useFacilityTypes } from '../hooks/useFacilities';
import { exportToExcel } from '../lib/excel-export';
import type { LocationRankingItem } from '../types/api';
import { Button } from './ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { Input } from './ui/Input';
import { Table, type TableColumn } from './ui/Table';

interface LocationRankingProps {
  className?: string;
}

export default function LocationRanking({ className }: LocationRankingProps) {
  const { dictionary } = useLocale();
  const [periodMode, setPeriodMode] = useState<'week' | 'dateRange'>('week');
  const [filters, setFilters] = useState<{
    locationType?: string;
    parentLocationId?: string;
    weekNumber?: number;
    year?: number;
    startDate?: string;
    endDate?: string;
    facilityTypeId?: string;
  }>({
    weekNumber: new Date().getWeek(),
    year: new Date().getFullYear(),
  });

  const apiParams = periodMode === 'dateRange' && filters.startDate && filters.endDate
    ? { ...filters, startDate: filters.startDate, endDate: filters.endDate, weekNumber: undefined, year: undefined }
    : { ...filters, startDate: undefined, endDate: undefined };

  // Breadcrumb navigation state
  const [breadcrumb, setBreadcrumb] = useState<
    Array<{
      id: string;
      name: string;
      type: string;
    }>
  >([]);

  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [exportLoading, setExportLoading] = useState<string | null>(null);
  const exportDropdownRef = useRef<HTMLDivElement>(null);

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setShowExportDropdown(false);
      }
    };

    if (showExportDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportDropdown]);

  const { data: rankingData, isLoading, error } = useLocationRanking(apiParams);
  const { data: facilityTypes } = useFacilityTypes();

  const handleFilterChange = (key: string, value: string | number) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined,
    }));
  };

  const clearFilters = () => {
    setPeriodMode('week');
    setFilters({
      weekNumber: new Date().getWeek(),
      year: new Date().getFullYear(),
      startDate: undefined,
      endDate: undefined,
    });
    setBreadcrumb([]);
  };

  // Handle expanding to view sublocations
  const handleExpandLocation = (location: LocationRankingItem) => {
    // Add current location to breadcrumb
    setBreadcrumb(prev => [
      ...prev,
      {
        id: location.locationId,
        name: location.locationName,
        type: location.locationType,
      },
    ]);

    // Update filters to show sublocations
    setFilters(prev => ({
      ...prev,
      parentLocationId: location.locationId,
      locationType: getChildLocationType(location.locationType),
    }));
  };

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = (index: number) => {
    const newBreadcrumb = breadcrumb.slice(0, index + 1);
    setBreadcrumb(newBreadcrumb);

    if (index === -1) {
      // Go back to root
      setFilters(prev => ({
        ...prev,
        parentLocationId: undefined,
        locationType: undefined,
      }));
    } else {
      const targetLocation = newBreadcrumb[newBreadcrumb.length - 1];
      setFilters(prev => ({
        ...prev,
        parentLocationId: targetLocation.id,
        locationType: getChildLocationType(targetLocation.type),
      }));
    }
  };

  // Get child location type based on parent type
  const getChildLocationType = (parentType: string): string => {
    const hierarchy: { [key: string]: string } = {
      PROVINCE: 'DISTRICT',
      DISTRICT: 'SECTOR',
      SECTOR: 'CELL',
      CELL: 'VILLAGE',
      VILLAGE: 'VILLAGE', // Village is the lowest level
    };
    return hierarchy[parentType] || 'DISTRICT';
  };

  // Check if a location can be expanded (has child locations)
  const canExpand = (locationType: string): boolean => {
    return locationType !== 'VILLAGE';
  };

  // Helper function to get rank badge color
  const getRankBadgeColor = (rank: number | null): string => {
    if (rank === null) return 'bg-gray-100 text-gray-600';
    if (rank === 1) return 'bg-yellow-100 text-yellow-800';
    if (rank === 2) return 'bg-gray-100 text-gray-800';
    if (rank === 3) return 'bg-orange-100 text-orange-800';
    return 'bg-secondary text-primary';
  };

  // Helper function to get score color
  const getScoreColor = (score: number): string => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-amber-600';
    if (score >= 40) return 'text-orange-500';
    return 'text-red-500';
  };

  // Helper function to get coverage color
  const getCoverageColor = (coverage: number): string => {
    if (coverage >= 80) return 'text-green-600';
    if (coverage >= 60) return 'text-amber-600';
    if (coverage >= 40) return 'text-orange-500';
    return 'text-red-500';
  };

  // Handle Excel export
  const handleExportExcel = () => {
    if (!rankingData?.rankings?.length) return;

    setExportLoading('excel');
    setShowExportDropdown(false);

    try {
      const exportData = rankingData.rankings.map((item: LocationRankingItem) => ({
        rank: item.rank || '-',
        locationName: item.locationName,
        locationType: item.locationType,
        averageScore: `${item.averageScore.toFixed(1)}%`,
        cleanPercentage: `${item.cleanPercentage.toFixed(1)}%`,
        finalScore: `${item.finalScore.toFixed(1)}%`,
        coveragePercentage: `${item.coveragePercentage.toFixed(1)}%`,
        assessedFacilities: item.assessedFacilities,
        totalFacilities: item.totalFacilities,
        totalAssessments: item.totalAssessments,
        above70Count: item.above70Count ?? 0,
        above70Pct: `${(item.above70Pct ?? 0).toFixed(1)}%`,
        below70Count: item.below70Count ?? 0,
        below70Pct: `${(item.below70Pct ?? 0).toFixed(1)}%`,
        reassessmentCount: item.reassessmentCount ?? 0,
      }));

      const columns = [
        { key: 'rank', label: 'Rank' },
        { key: 'locationName', label: 'Location Name' },
        { key: 'locationType', label: 'Type' },
        { key: 'averageScore', label: 'Average Score' },
        { key: 'finalScore', label: 'Final Score' },
        { key: 'coveragePercentage', label: 'Coverage %' },
        { key: 'assessedFacilities', label: 'Assessed Facilities' },
        { key: 'totalFacilities', label: 'Total Facilities' },
        { key: 'totalAssessments', label: 'Total Assessments' },
        { key: 'above70Count', label: 'Above 70 Count' },
        { key: 'above70Pct', label: 'Above 70 (%)' },
        { key: 'below70Count', label: 'Below 70 Count' },
        { key: 'below70Pct', label: 'Below 70 (%)' },
        { key: 'reassessmentCount', label: 'Re-inspections' },
      ];

      const locationContext = rankingData.parentLocationName || 'National Level';
      const title = `NHIP - Location Rankings`;
      const periodLabel = rankingData.startDate && rankingData.endDate
        ? `${rankingData.startDate} – ${rankingData.endDate}`
        : `Week ${rankingData.weekNumber} of ${rankingData.year}`;
      const subtitle = `${locationContext} | ${periodLabel} | ${rankingData.totalLocations} locations`;

      exportToExcel({
        fileName: rankingData.startDate && rankingData.endDate
          ? `Location_Rankings_${rankingData.startDate}_${rankingData.endDate}`
          : `Location_Rankings_Week${rankingData.weekNumber}_${rankingData.year}`,
        sheetName: 'Rankings',
        data: exportData,
        columns,
        title,
        subtitle,
      });
    } catch (error) {
      console.error('Excel export failed:', error);
    } finally {
      setExportLoading(null);
    }
  };

  // Handle PDF export
  const handleExportPDF = () => {
    if (!rankingData?.rankings?.length) return;

    setExportLoading('pdf');
    setShowExportDropdown(false);

    try {
      const doc = new jsPDF();
      const margin = 20;
      let yPosition = 20;

      const locationContext = rankingData.parentLocationName || 'National Level';

      // Title
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('NHIP - Location Rankings', margin, yPosition);
      yPosition += 10;

      const periodLabel = rankingData.startDate && rankingData.endDate
        ? `${rankingData.startDate} – ${rankingData.endDate}`
        : `Week ${rankingData.weekNumber} of ${rankingData.year}`;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`${locationContext} | ${periodLabel}`, margin, yPosition);
      yPosition += 8;
      doc.text(`${rankingData.totalLocations} locations`, margin, yPosition);
      yPosition += 15;

      // Table - add both avg score and final score columns here
      const tableData = rankingData.rankings.map((item: LocationRankingItem) => [
        item.rank?.toString() || '-',
        item.locationName,
        item.locationType,
        `${item.averageScore.toFixed(1)}%`,
        `${item.finalScore.toFixed(1)}%`,
        `${item.assessedFacilities}/${item.totalFacilities}`,
        item.totalAssessments.toString(),
        `${(item.above70Count ?? 0)} (${(item.above70Pct ?? 0).toFixed(1)}%)`,
        `${(item.below70Count ?? 0)} (${(item.below70Pct ?? 0).toFixed(1)}%)`,
      ]);

      autoTable(doc, {
        startY: yPosition,
        head: [['Rank', 'Location', 'Type', 'Avg Score', 'Final Score', 'Facilities', 'Assessments', '≥70%', '<70%']],
        body: tableData,
        margin: { left: margin, right: margin },
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [27, 107, 69] },
      });

      const timestamp = new Date().toISOString().split('T')[0];
      const fileSuffix = rankingData.startDate && rankingData.endDate
        ? `${rankingData.startDate}_${rankingData.endDate}`
        : `Week${rankingData.weekNumber}_${rankingData.year}`;
      doc.save(`Location_Rankings_${fileSuffix}_${timestamp}.pdf`);
    } catch (error) {
      console.error('PDF export failed:', error);
    } finally {
      setExportLoading(null);
    }
  };

  const rankingColumns = useMemo<TableColumn<LocationRankingItem>[]>(() => [
    {
      key: 'rank',
      label: 'Rank',
      render: item => item.rank ? (
        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-medium ${getRankBadgeColor(item.rank)}`}>
          {item.rank}
        </span>
      ) : (
        <span className='inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-medium bg-gray-100 text-gray-600'>—</span>
      ),
    },
    {
      key: 'locationName',
      label: 'Location',
      sortable: true,
      render: item => (
        <div>
          <div className='font-medium text-gray-900'>{item.locationName}</div>
          <div className='text-xs text-gray-400'>{item.locationType}</div>
        </div>
      ),
    },
    {
      key: 'averageScore',
      label: 'Average Score',
      sortable: true,
      render: item => (
        <span className={`font-semibold ${getScoreColor(item.averageScore)}`}>
          {item.averageScore.toFixed(1)}%
        </span>
      ),
    },
    {
      key: 'finalScore',
      label: 'Final Score',
      sortable: true,
      render: item => (
        <span className={`font-semibold ${getScoreColor(item.finalScore)}`}>
          {item.finalScore.toFixed(1)}%
        </span>
      ),
    },
    {
      key: 'coveragePercentage',
      label: 'Facility Coverage',
      sortable: true,
      headerClassName: 'hidden md:table-cell',
      cellClassName: 'hidden md:table-cell',
      render: item => (
        <div className='flex items-center gap-2'>
          <span className={`font-medium text-xs ${getCoverageColor(item.coveragePercentage)}`}>
            {item.coveragePercentage.toFixed(1)}%
          </span>
          <div className='w-16 bg-gray-200 rounded-full h-1.5'>
            <div
              className={`h-1.5 rounded-full ${
                item.coveragePercentage >= 80 ? 'bg-green-500'
                  : item.coveragePercentage >= 60 ? 'bg-yellow-500'
                  : item.coveragePercentage >= 40 ? 'bg-orange-500'
                  : 'bg-red-500'
              }`}
              style={{ width: `${Math.min(item.coveragePercentage, 100)}%` }}
            />
          </div>
        </div>
      ),
    },
    {
      key: 'assessedFacilities',
      label: 'Assessed / Total',
      headerClassName: 'hidden lg:table-cell',
      cellClassName: 'hidden lg:table-cell',
      render: item => (
        <span className='text-gray-600 text-xs'>{item.assessedFacilities} / {item.totalFacilities}</span>
      ),
    },
    {
      key: 'above70Count',
      label: 'Above 70%',
      sortable: true,
      headerClassName: 'hidden lg:table-cell',
      cellClassName: 'hidden lg:table-cell',
      render: item => (
        <div className='flex items-center gap-1.5'>
          <span className='text-xs font-semibold text-green-600'>{(item.above70Count ?? 0).toLocaleString()}</span>
          <span className='text-[10px] text-gray-400'>{(item.above70Pct ?? 0).toFixed(1)}%</span>
        </div>
      ),
    },
    {
      key: 'below70Count',
      label: 'Below 70%',
      sortable: true,
      headerClassName: 'hidden lg:table-cell',
      cellClassName: 'hidden lg:table-cell',
      render: item => (
        <div className='flex items-center gap-1.5'>
          <span className='text-xs font-semibold text-red-500'>{(item.below70Count ?? 0).toLocaleString()}</span>
          <span className='text-[10px] text-gray-400'>{(item.below70Pct ?? 0).toFixed(1)}%</span>
        </div>
      ),
    },
    {
      key: 'totalAssessments',
      label: 'Inspections',
      sortable: true,
      headerClassName: 'hidden lg:table-cell',
      cellClassName: 'hidden lg:table-cell',
      render: item => (
        <span className='text-gray-600 text-xs'>{item.totalAssessments}</span>
      ),
    },
    {
      key: 'reassessmentCount',
      label: 'Re-inspections',
      sortable: true,
      headerClassName: 'hidden lg:table-cell',
      cellClassName: 'hidden lg:table-cell',
      render: item => (
        <span className='text-gray-600 text-xs'>{item.reassessmentCount ?? 0}</span>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: item => canExpand(item.locationType) ? (
        <button
          onClick={() => handleExpandLocation(item)}
          className='inline-flex items-center gap-1 text-primary hover:text-primary/80 text-xs font-medium'
          title={`View sub-locations of ${item.locationName}`}
        >
          <ChevronRight className='h-4 w-4' />
          <span className='hidden sm:inline'>Drill down</span>
        </button>
      ) : (
        <span className='text-gray-300 text-xs'>—</span>
      ),
    },
  ], [getRankBadgeColor, getScoreColor, getCoverageColor, canExpand, handleExpandLocation]);

  if (error) {
    return (
      <div className='bg-white rounded-lg shadow-sm'>
        <div className='flex items-center justify-center h-64 p-4 sm:p-6'>
          <div className='text-center'>
            <p className='text-red-600 mb-2'>Error loading location rankings</p>
            <p className='text-sm text-gray-500'>{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='bg-white rounded-lg shadow-sm'>
      <div className={`${className || ''} space-y-4 sm:space-y-6 p-4 sm:p-6`}>
        {/* Header with Filters */}
        <Card className='w-full'>
          <CardHeader className='pb-2 sm:pb-3 pt-3 sm:pt-6'>
            <CardTitle className='flex items-center gap-2 text-base sm:text-lg'>
              <Award className='h-4 w-4 sm:h-5 sm:w-5' />
              {dictionary['location_ranking.title']}
            </CardTitle>
            <CardDescription className='text-xs sm:text-sm'>
              {dictionary['location_ranking.description']}
            </CardDescription>
          </CardHeader>
          <CardContent className='pb-3 sm:pb-6'>
            <div className='flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4 sm:items-end'>
              <div className='grid grid-cols-2 sm:flex gap-2 sm:gap-2 flex-wrap'>
                <div className='col-span-2 sm:col-span-1 flex gap-2 items-end'>
                  <Input
                    type='select'
                    aria-label='Period'
                    value={periodMode}
                    onChange={e => setPeriodMode(e.target.value as 'week' | 'dateRange')}
                    options={[
                      { value: 'week', label: dictionary['assessments.by_week'] ?? 'By week' },
                      { value: 'dateRange', label: dictionary['assessments.by_date_range'] ?? 'Date range' },
                    ]}
                    className='w-auto min-w-[140px] text-sm'
                  />
                </div>
                {periodMode === 'week' && (
                  <>
                    <div>
                      <label className='block text-xs font-medium text-gray-700 mb-1'>
                        {dictionary['location_ranking.week']}
                      </label>
                      <Input
                        type='number'
                        min='1'
                        max='53'
                        value={filters.weekNumber ?? ''}
                        onChange={e => handleFilterChange('weekNumber', parseInt(e.target.value) || 0)}
                        className='w-full sm:w-20 text-sm'
                        placeholder={dictionary['location_ranking.week']}
                      />
                    </div>
                    <div>
                      <label className='block text-xs font-medium text-gray-700 mb-1'>
                        {dictionary['location_ranking.year']}
                      </label>
                      <Input
                        type='number'
                        min='2020'
                        max='2030'
                        value={filters.year ?? ''}
                        onChange={e => handleFilterChange('year', parseInt(e.target.value) || 0)}
                        className='w-full sm:w-24 text-sm'
                        placeholder={dictionary['location_ranking.year']}
                      />
                    </div>
                  </>
                )}
                {periodMode === 'dateRange' && (
                  <>
                    <div>
                      <label className='block text-xs font-medium text-gray-700 mb-1'>
                        {dictionary['dashboard.start_date'] ?? 'Start'}
                      </label>
                      <Input
                        type='date'
                        value={filters.startDate ?? ''}
                        onChange={e => handleFilterChange('startDate', e.target.value)}
                        className='w-full sm:w-36 text-sm'
                      />
                    </div>
                    <div>
                      <label className='block text-xs font-medium text-gray-700 mb-1'>
                        {dictionary['dashboard.end_date'] ?? 'End'}
                      </label>
                      <Input
                        type='date'
                        value={filters.endDate ?? ''}
                        onChange={e => handleFilterChange('endDate', e.target.value)}
                        className='w-full sm:w-36 text-sm'
                      />
                    </div>
                  </>
                )}
                <div className='col-span-2 sm:col-span-1'>
                  <label className='block text-xs font-medium text-gray-700 mb-1'>
                    {dictionary['location_ranking.location_type']}
                  </label>
                  <Input
                    type='select'
                    value={filters.locationType || ''}
                    onChange={e => handleFilterChange('locationType', e.target.value as string)}
                    placeholder={dictionary['location_ranking.all_types']}
                    options={[
                      { value: 'PROVINCE', label: dictionary['location.option_province'] },
                      { value: 'DISTRICT', label: dictionary['location.option_district'] },
                      { value: 'SECTOR', label: dictionary['location.option_sector'] },
                      { value: 'CELL', label: dictionary['location.option_cell'] },
                      { value: 'VILLAGE', label: dictionary['location.option_village'] },
                    ]}
                    className='w-full sm:w-32 text-sm'
                  />
                </div>
                <div className='col-span-2 sm:col-span-1'>
                  <label className='block text-xs font-medium text-gray-700 mb-1'>
                    {dictionary['location_ranking.facility_type']}
                  </label>
                  <Input
                    type='select'
                    value={filters.facilityTypeId || ''}
                    onChange={e => handleFilterChange('facilityTypeId', e.target.value as string)}
                    placeholder={dictionary['location_ranking.all_facility_types']}
                    options={(facilityTypes ?? []).map(type => ({ value: type.id, label: type.name }))}
                    className='w-full sm:w-40 text-sm'
                  />
                </div>
              </div>
              <Button onClick={clearFilters} variant='outline' size='sm' className='w-full sm:w-auto text-sm'>
                {dictionary['location_ranking.clear_filters']}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Breadcrumb Navigation */}
        {breadcrumb.length > 0 && (
          <Card className='w-full'>
            <CardContent className='py-2 sm:py-3'>
              <div className='flex items-center gap-1 sm:gap-2 text-xs sm:text-sm overflow-x-auto'>
                <button
                  onClick={() => handleBreadcrumbClick(-1)}
                  className='flex items-center gap-1 text-primary hover:text-primary/80 font-medium whitespace-nowrap flex-shrink-0'
                >
                  <Home className='h-3 w-3 sm:h-4 sm:w-4' />
                  <span className='hidden sm:inline'>{dictionary['location_ranking.all_locations']}</span>
                  <span className='sm:hidden'>Home</span>
                </button>
                {breadcrumb.map((item, index) => (
                  <div key={item.id} className='flex items-center gap-1 sm:gap-2 flex-shrink-0'>
                    <span className='text-gray-400'>/</span>
                    <button
                      onClick={() => handleBreadcrumbClick(index)}
                      className='text-primary hover:text-primary/80 font-medium whitespace-nowrap'
                    >
                      {item.name}
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Ranking Results */}
        <Card className='w-full'>
          <CardHeader className='pb-2 sm:pb-3 pt-3 sm:pt-6'>
            <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
              <div className='flex-1 min-w-0'>
                <CardTitle className='text-base sm:text-lg'>
                  {rankingData?.parentLocationName
                    ? `${rankingData.parentLocationName} ${dictionary['location_ranking.rankings']}`
                    : dictionary['location_ranking.rankings']}
                </CardTitle>
                <CardDescription className='text-xs sm:text-sm'>
                  {rankingData?.startDate && rankingData?.endDate
                    ? `${rankingData.startDate} – ${rankingData.endDate}`
                    : `${dictionary['location_ranking.week_of']} ${rankingData?.weekNumber} ${dictionary['location_ranking.of']} ${rankingData?.year}`}{' '}
                  • {rankingData?.totalLocations} {dictionary['location_ranking.locations']}
                  {rankingData?.childLocationType && ` • ${rankingData.childLocationType.toLowerCase()}s`}
                </CardDescription>
              </div>
              <div className='flex items-center gap-2 sm:gap-4 flex-shrink-0'>
                {rankingData?.childLocationType && (
                  <div className='hidden md:block text-xs sm:text-sm text-gray-600'>
                    {dictionary['location_ranking.ranking_by']} {rankingData.childLocationType}
                  </div>
                )}
                {rankingData?.rankings?.length > 0 && (
                  <div className='relative' ref={exportDropdownRef}>
                    <Button
                      onClick={() => setShowExportDropdown(!showExportDropdown)}
                      variant='outline'
                      size='sm'
                      className='flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-primary hover:text-primary/80 border-none shadow-none hover:bg-secondary py-1 px-2 sm:px-3'
                      disabled={exportLoading !== null}
                    >
                      <Download className='h-3 w-3 sm:h-4 sm:w-4' />
                      <span className='hidden sm:inline'>{exportLoading ? 'Exporting...' : 'Export'}</span>
                      <span className='sm:hidden'>{exportLoading ? '...' : 'Export'}</span>
                    </Button>

                    {/* Export Dropdown Menu */}
                    {showExportDropdown && (
                      <div className='absolute right-0 top-full mt-1 w-40 sm:w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50'>
                        <div className='p-2'>
                          <button
                            onClick={handleExportExcel}
                            disabled={exportLoading !== null}
                            className='w-full flex items-center gap-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm text-green-700 hover:bg-green-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                          >
                            <FileText className='h-3 w-3 sm:h-4 sm:w-4' />
                            <span>Excel</span>
                          </button>
                          <button
                            onClick={handleExportPDF}
                            disabled={exportLoading !== null}
                            className='w-full flex items-center gap-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm text-red-700 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                          >
                            <FileText className='h-3 w-3 sm:h-4 sm:w-4' />
                            <span>PDF</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className='p-0'>
            <Table
              columns={rankingColumns}
              data={rankingData?.rankings ?? []}
              isLoading={isLoading}
              enableSearch={false}
              enablePagination={false}
              showHeader={false}
              emptyMessage={dictionary['location_ranking.no_data']}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Helper function to get week number
declare global {
  interface Date {
    getWeek(): number;
  }
}

Date.prototype.getWeek = function () {
  const date = new Date(this.getTime());
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  const week1 = new Date(date.getFullYear(), 0, 4);
  return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
};
